/**
 * Le chiavi della cache di react-query, in un posto solo.
 *
 * Erano stringhe scritte a mano in cinque file, e il guasto non è estetico:
 * l'invalidazione **incrocia**. Dopo aver scritto un'operatrice si rilegge
 * anche `['flusso']`, perché le colonne della plancia sono quelle righe; dopo
 * aver salvato il calendario pure, perché da quegli orari dipendono la striscia
 * delle ore e lo stato del turno. Chi scriveva `['flussi']` per sbaglio non
 * otteneva un errore: otteneva una schermata che continuava a mostrare chi è
 * appena uscito dall'organico, ed è così che qualcuno gli assegna un ordine.
 *
 * Da qui in poi un refuso non compila.
 *
 * La forma è quella consigliata da TanStack Query: chiavi gerarchiche, dalla
 * più larga alla più stretta. `chiavi.flusso()` invalida **tutte** le viste di
 * flusso, `chiavi.flusso('pianificazione')` solo quella — ed è la stessa
 * distinzione che il codice faceva già a mano, scritta una volta.
 */
export const chiavi = {
    /** Le viste delle fasi a valle. Senza argomento: tutte quante. */
    flusso: (vista) => vista === undefined ? ['flusso'] : ['flusso', vista],
    /** La vista del banco di ricezione, che è **per postazione**. */
    banco: (postazione) => ['vista', postazione],
    /** Le bolle che il banco può aprire, per postazione. */
    attesi: (postazione) => ['attesi', postazione],
    /** L'organico, come lo tiene l'ufficio. */
    operatrici: () => ['operatrici'],
    /**
     * L'archivio degli esecutivi. I filtri entrano nella chiave: due ricerche
     * diverse sono due risposte diverse, e senza di loro la seconda mostrerebbe
     * il risultato della prima finché la cache non scade.
     */
    esecutivi: (cerca, processo) => ['esecutivi', cerca ?? '', processo ?? ''],
    /**
     * L'anagrafica dei fallati. Come gli esecutivi, i filtri entrano nella
     * chiave: «da prendere in carico» e «tutte» sono due risposte diverse.
     *
     * Senza argomenti invalida **entrambe**, ed è quello che serve dopo una presa
     * in carico: la riga esce da una lista ed entra nell'altra.
     */
    fallati: (aperti, cerca) => aperti === undefined ? ['fallati'] : ['fallati', aperti, cerca ?? ''],
    /** Gli scarti di lavorazione. Stessa forma dei fallati, e per la stessa ragione. */
    scarti: (aperti, cerca) => aperti === undefined ? ['scarti'] : ['scarti', aperti, cerca ?? ''],
    /**
     * Le giacenze. I tre filtri entrano nella chiave: «tutte», «solo
     * scostamenti» e «solo sotto scorta» sono tre risposte diverse, e senza di
     * loro la seconda mostrerebbe il risultato della prima.
     */
    giacenze: (cerca, scostamenti, sottoScorta) => ['giacenze', cerca ?? '', scostamenti ?? false, sottoScorta ?? false],
    /** Le impostazioni d'ufficio. */
    /**
     * The shop shelf. The search is in the key; without it, every search —
     * which is what a count must refresh, since it changes every view.
     */
    scaffale: (cerca) => cerca === undefined ? ['scaffale'] : ['scaffale', cerca],
    impostazioni: () => ['impostazioni'],
    /** The latest PS130 weighings: one answer, no filters. */
    pesate: () => ['pesate'],
    /** The DecoNetwork write journal, per filter. */
    scrittureDeco: (filtro) => ['scrittureDeco', filtro],
    /**
     * The loading missions (rule 46): the list, or one mission. Without a code,
     * every one of them — what a save or a send must refresh.
     */
    missioniCarico: (codice) => codice === undefined ? ['missioni-carico'] : ['missioni-carico', codice],
    /**
     * The equipment registry: which crates and trolleys exist, and the
     * capacities. One key for the whole answer, since the API returns it whole.
     */
    attrezzatura: () => ['attrezzatura'],
    /** Il calendario di lavoro. */
    turni: () => ['turni'],
    /** Le identità: chi entra e con che ruoli. */
    utenti: () => ['utenti'],
};
//# sourceMappingURL=keys.js.map